/**
 * HeroImagePicker — reusable admin control for hero / slide background images.
 * Three ways to set an image:
 *   1) Upload from device (POST /admin/home/upload-image)
 *   2) AI-generate from prompt (POST /admin/home/generate-image, Nano Banana)
 *   3) Paste an external URL (fallback)
 * Shows a live preview and returns the resolved URL via onChange(url).
 */
import { useRef, useState } from "react";
import { Upload, Sparkles, Link as LinkIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const resolvePreview = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const inputCls =
  "w-full bg-transparent border border-hairline rounded-lg px-3 py-2.5 text-sm text-forest focus:outline-none focus:border-forest transition-colors";

export const HeroImagePicker = ({ value, onChange, api, label = "Image", testidPrefix = "hero-img", aspect = "16/9", portrait = false, aiKind = "hero" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const previewAspect = portrait ? "1/1" : aspect;
  const previewMaxW = portrait ? "max-w-[160px]" : "";
  const aiTitle = portrait ? "AI generate headshot" : "AI generate hero image";
  const aiPlaceholder = portrait
    ? 'Describe the person, e.g. "professional female founder, warm smile, ivory kurta, studio lighting"'
    : 'Describe the scene, e.g. "Ivory linen bedroom with champagne throw and morning light, monsoon rain outside the window"';

  const doUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) return toast.error("Only image files");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/home/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        onChange(data.url);
        toast.success("Uploaded — save the homepage to apply");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doGenerate = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 6) return toast.error("Give the AI a bit more to go on (6+ characters)");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/home/generate-image", { prompt, kind: aiKind });
      if (data?.url) {
        onChange(data.url);
        toast.success("Rendered — save the homepage to apply");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — try again");
    } finally {
      setGenerating(false);
    }
  };

  const clearImage = () => onChange("");

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-picker`}>
      <div className="overline text-forest/60">{label}</div>

      {/* Preview */}
      <div
        className={`border border-hairline rounded-lg overflow-hidden bg-cream/40 relative ${previewMaxW}`}
        style={{ aspectRatio: previewAspect }}
      >
        {value ? (
          <>
            <img
              src={resolvePreview(value)}
              alt="Preview"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`${testidPrefix}-preview`}
            />
            <button
              type="button"
              onClick={clearImage}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white text-forest flex items-center justify-center border border-hairline"
              data-testid={`${testidPrefix}-clear`}
              aria-label="Clear image"
            >
              <X size={13} strokeWidth={1.8} />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-forest/40 text-xs italic">
            No image set — upload, generate, or paste a URL
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
          data-testid={`${testidPrefix}-file`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || generating}
          className="inline-flex items-center gap-2 overline text-forest border border-hairline rounded-full px-4 py-2 text-xs hover:border-forest disabled:opacity-40"
          data-testid={`${testidPrefix}-upload-btn`}
        >
          {uploading ? <Loader2 size={12} strokeWidth={1.8} className="animate-spin" /> : <Upload size={12} strokeWidth={1.8} />}
          {uploading ? "Uploading…" : "Upload from device"}
        </button>
        <button
          type="button"
          onClick={() => setShowUrl((v) => !v)}
          className={`inline-flex items-center gap-2 overline text-xs rounded-full px-4 py-2 border ${showUrl ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest"}`}
          data-testid={`${testidPrefix}-url-toggle`}
        >
          <LinkIcon size={12} strokeWidth={1.8} /> Paste URL
        </button>
      </div>

      {/* URL fallback */}
      {showUrl && (
        <input
          className={inputCls}
          placeholder="https://images.unsplash.com/…"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testidPrefix}-url-input`}
        />
      )}

      {/* AI generator */}
      <div className="border border-hairline rounded-lg p-3 bg-cream/40 space-y-2">
        <div className="overline text-gold text-[10px] tracking-[3px] flex items-center gap-1.5">
          <Sparkles size={11} strokeWidth={1.8} /> {aiTitle}
        </div>
        <textarea
          className={`${inputCls} min-h-[64px] resize-y`}
          placeholder={aiPlaceholder}
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          data-testid={`${testidPrefix}-ai-prompt`}
        />
        <button
          type="button"
          onClick={doGenerate}
          disabled={generating || uploading || aiPrompt.trim().length < 6}
          className="inline-flex items-center gap-2 overline text-white bg-forest hover:bg-[hsl(150_60%_16%)] transition-colors rounded-full px-4 py-2 text-xs tracking-[3px] disabled:opacity-40"
          data-testid={`${testidPrefix}-ai-generate`}
        >
          {generating ? <Loader2 size={12} strokeWidth={1.8} className="animate-spin" /> : <Sparkles size={12} strokeWidth={1.8} />}
          {generating ? "Rendering (Nano Banana)…" : "Generate with AI"}
        </button>
        <p className="text-forest/40 text-[11px] italic">One image per generation. Takes ~15–30 s. Uses the Anabiya brand style automatically.</p>
      </div>
    </div>
  );
};

export default HeroImagePicker;
